import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  BookOpenIcon,
  UsersIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts'
import { analyticsAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { format } from 'date-fns'

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

const Analytics: React.FC = () => {
  const [conversationDays, setConversationDays] = useState(30)
  const [engagementDays] = useState(30)
  const [performanceDays, setPerformanceDays] = useState(7)

  // Fetch dashboard overview
  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: () => analyticsAPI.getDashboard().then(res => res.data),
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  // Fetch conversation analytics
  const { data: conversationsData, isLoading: isConversationsLoading } = useQuery({
    queryKey: ['analytics-conversations', conversationDays],
    queryFn: () => analyticsAPI.getConversations(conversationDays).then(res => res.data),
  })

  // Fetch engagement analytics
  const { data: engagementData, isLoading: isEngagementLoading } = useQuery({
    queryKey: ['analytics-engagement', engagementDays],
    queryFn: () => analyticsAPI.getEngagement(engagementDays).then(res => res.data),
  })

  // Fetch topics
  const { data: topicsData } = useQuery({
    queryKey: ['analytics-topics', 30],
    queryFn: () => analyticsAPI.getTopics(30).then(res => res.data),
  })

  // Fetch knowledge analytics
  const { data: knowledgeData } = useQuery({
    queryKey: ['analytics-knowledge'],
    queryFn: () => analyticsAPI.getKnowledge().then(res => res.data),
  })

  // Fetch performance metrics
  const { data: performanceData } = useQuery({
    queryKey: ['analytics-performance', performanceDays],
    queryFn: () => analyticsAPI.getPerformance(performanceDays).then(res => res.data),
  })

  // Fetch recent activity
  const { data: activityData } = useQuery({
    queryKey: ['analytics-activity'],
    queryFn: () => analyticsAPI.getActivity(10).then(res => res.data),
    refetchInterval: 10000,
  })

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    if (minutes < 1) return `${seconds}s`
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    return `${hours}h ${minutes % 60}m`
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'conversation':
        return <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary" />
      case 'inquiry':
        return <EnvelopeIcon className="h-5 w-5 text-green-500" />
      case 'knowledge':
        return <BookOpenIcon className="h-5 w-5 text-purple-500" />
      default:
        return <SparklesIcon className="h-5 w-5 text-muted-foreground" />
    }
  }

  if (isDashboardLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ChartBarIcon className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
            <p className="text-sm text-muted-foreground">
              Insights and performance metrics
            </p>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Conversations Card */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversations</p>
                <p className="text-2xl font-bold text-foreground">
                  {dashboardData.conversations.total.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboardData.conversations.active} active
                </p>
              </div>
              <ChatBubbleLeftRightIcon className="h-10 w-10 text-primary opacity-80" />
            </div>
            <div className="mt-3 flex items-center text-xs">
              <span className="text-green-600 flex items-center">
                <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                {dashboardData.conversations.today} today
              </span>
            </div>
          </div>

          {/* Inquiries Card */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Inquiries</p>
                <p className="text-2xl font-bold text-foreground">
                  {dashboardData.inquiries.total.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboardData.inquiries.pending} pending
                </p>
              </div>
              <EnvelopeIcon className="h-10 w-10 text-green-500 opacity-80" />
            </div>
            <div className="mt-3 flex items-center text-xs">
              <span className="text-green-600 flex items-center">
                <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                {dashboardData.inquiries.today} today
              </span>
            </div>
          </div>

          {/* Knowledge Base Card */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Knowledge Base</p>
                <p className="text-2xl font-bold text-foreground">
                  {dashboardData.knowledge.total.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {dashboardData.knowledge.active} active
                </p>
              </div>
              <BookOpenIcon className="h-10 w-10 text-purple-500 opacity-80" />
            </div>
          </div>

          {/* Users Card */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold text-foreground">
                  {dashboardData.users.total.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Admin accounts
                </p>
              </div>
              <UsersIcon className="h-10 w-10 text-orange-500 opacity-80" />
            </div>
          </div>
        </div>
      )}

      {/* Conversation Trends */}
      {conversationsData && !isConversationsLoading && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">Conversation Trends</h2>
            <select
              className="input-field text-sm w-auto min-w-[9rem]"
              value={conversationDays}
              onChange={(e) => setConversationDays(parseInt(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          {conversationsData.daily && conversationsData.daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={conversationsData.daily}>
                <defs>
                  <linearGradient id="colorConversations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="conversations"
                  stroke="#3B82F6"
                  fillOpacity={1}
                  fill="url(#colorConversations)"
                  name="Conversations"
                />
                <Area
                  type="monotone"
                  dataKey="uniqueUsers"
                  stroke="#10B981"
                  fillOpacity={1}
                  fill="url(#colorUsers)"
                  name="Unique Users"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              No conversation data available
            </div>
          )}

          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
            <div>
              <p className="text-sm text-muted-foreground">Total Messages</p>
              <p className="text-xl font-semibold text-foreground">
                {conversationsData.messages?.totalMessages?.toLocaleString() || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Messages/Conv</p>
              <p className="text-xl font-semibold text-foreground">
                {conversationsData.messages?.avgMessages?.toFixed(1) || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Duration</p>
              <p className="text-xl font-semibold text-foreground">
                {formatDuration(conversationsData.messages?.avgDuration || 0)}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Distribution */}
        {engagementData && !isEngagementLoading && (
          <div className="card">
            <h2 className="text-lg font-semibold text-foreground mb-4">Device Distribution</h2>
            {engagementData.devices && engagementData.devices.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={engagementData.devices}
                    dataKey="count"
                    nameKey="device"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ device, percent }) =>
                      `${device}: ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {engagementData.devices.map((_entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-muted-foreground py-8">No device data available</div>
            )}
          </div>
        )}

        {/* Hourly Usage Patterns */}
        {engagementData && !isEngagementLoading && (
          <div className="card">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Usage by Hour of Day
            </h2>
            {engagementData.hourlyUsage && engagementData.hourlyUsage.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={engagementData.hourlyUsage}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" tickFormatter={(hour) => `${hour}:00`} />
                  <YAxis />
                  <Tooltip labelFormatter={(hour) => `${hour}:00 - ${hour}:59`} />
                  <Bar dataKey="count" fill="#3B82F6" name="Conversations" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-muted-foreground py-8">No hourly data available</div>
            )}
          </div>
        )}
      </div>

      {/* Engagement Metrics */}
      {engagementData && !isEngagementLoading && (
        <div className="card">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            User Engagement Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-primary-50 rounded-lg">
              <p className="text-sm text-primary font-medium">Total Users</p>
              <p className="text-2xl font-bold text-primary-900">
                {engagementData.engagement?.totalUsers?.toLocaleString() || 0}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600 font-medium">Avg Sessions/User</p>
              <p className="text-2xl font-bold text-green-900">
                {engagementData.engagement?.avgSessionsPerUser?.toFixed(1) || 0}
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-600 font-medium">Avg Messages/User</p>
              <p className="text-2xl font-bold text-purple-900">
                {engagementData.engagement?.avgMessagesPerUser?.toFixed(1) || 0}
              </p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-orange-600 font-medium">Avg Session Duration</p>
              <p className="text-2xl font-bold text-orange-900">
                {formatDuration(engagementData.engagement?.avgSessionDuration || 0)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Session Duration Distribution */}
      {conversationsData && !isConversationsLoading && conversationsData.sessionDurations && (
        <div className="card">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Session Duration Distribution
          </h2>
          {conversationsData.sessionDurations.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={conversationsData.sessionDurations}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#10B981" name="Sessions" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              No session duration data available
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Topics */}
        {topicsData && topicsData.topics && (
          <div className="card">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Popular Topics (Last 30 Days)
            </h2>
            {topicsData.topics.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {topicsData.topics.slice(0, 10).map((topic: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-start justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {topic.topic}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Mentioned {topic.count} times
                      </p>
                    </div>
                    <span className="ml-2 px-2 py-1 text-xs font-semibold bg-primary/10 text-primary-800 rounded-full">
                      {topic.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">No topics data available</div>
            )}
          </div>
        )}

        {/* Recent Activity */}
        {activityData && activityData.activities && (
          <div className="card">
            <h2 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h2>
            {activityData.activities.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {activityData.activities.map((activity: any) => (
                  <div
                    key={`${activity.type}-${activity.id}`}
                    className="flex items-start space-x-3 p-3 hover:bg-muted/50 rounded-lg transition-colors"
                  >
                    <div className="flex-shrink-0 mt-1">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {activity.title}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {activity.description}
                      </p>
                      <p className="text-xs text-muted-foreground/70 mt-1">
                        {format(new Date(activity.timestamp), 'MMM dd, yyyy HH:mm')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No recent activity
              </div>
            )}
          </div>
        )}
      </div>

      {/* Performance Metrics */}
      {performanceData && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">System Health</h2>
            <select
              className="input-field text-sm w-auto min-w-[9rem]"
              value={performanceDays}
              onChange={(e) => setPerformanceDays(parseInt(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Total Conversations</p>
                <ClockIcon className="h-5 w-5 text-muted-foreground/70" />
              </div>
              <p className="text-2xl font-bold text-foreground mt-2">
                {performanceData.systemHealth?.totalConversations?.toLocaleString() || 0}
              </p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Active Now</p>
                <span className="h-3 w-3 bg-green-500 rounded-full animate-pulse" />
              </div>
              <p className="text-2xl font-bold text-foreground mt-2">
                {performanceData.systemHealth?.activeConversations?.toLocaleString() || 0}
              </p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Avg Msg/Conv</p>
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-muted-foreground/70" />
              </div>
              <p className="text-2xl font-bold text-foreground mt-2">
                {performanceData.systemHealth?.avgMessagesPerConversation?.toFixed(1) || 0}
              </p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Uptime</p>
                <ArrowTrendingUpIcon className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-2xl font-bold text-green-600 mt-2">
                {performanceData.systemHealth?.uptime || '99.9%'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Knowledge Base Analytics */}
      {knowledgeData && (
        <div className="card">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Knowledge Base Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {knowledgeData.byType && knowledgeData.byType.length > 0 ? (
              knowledgeData.byType.map((item: any, index: number) => (
                <div
                  key={index}
                  className="p-4 bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg"
                >
                  <p className="text-sm text-primary/90 font-medium uppercase">
                    {item.type}
                  </p>
                  <p className="text-3xl font-bold text-primary mt-2">
                    {item.count}
                  </p>
                  <p className="text-xs text-primary mt-1">documents</p>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center text-muted-foreground py-8">
                No knowledge base data available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Analytics
